import { type UISortOption } from 'src/components/types/UISortOption';
import { type ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectHash } from 'src/redux/navigation/selectors';
import { getSortOptionValue } from 'src/redux/productsQuery/helpers/getSortOptionValue';
import { selectListingsBasePathParts } from 'src/redux/productsQuery/selectors/selectors';
import { adaptStoreToFilterPath, adaptStoreToPageURL } from './adaptStoreToPageURL';
import { buildListingsBasePath } from './buildListingsBasePath';
import { buildListingsURL } from './buildListingsURL';
import { getListingsSortAsURLSearchParams } from './listings-api/getListingsSortAsURLSearchParams';

/**
 * Derives a URL href for each sort option based on current store state.
 * Each href preserves the current facet filters (as path segments) and search
 * term, resets pagination to page 1, and sets the `sort` param for that option
 * (or omits it for the default/empty-key sort option).
 */
export const adaptStoreToSortOptionHrefs = (state: ReduxApplicationState): UISortOption[] => {
  const { sortOptions } = state.productsQuery;

  // Filter path: current active facets as path segments (constant across all sort hrefs).
  const filterPath = adaptStoreToFilterPath(state);

  // Base query params: search term, page 1 (no page param). Sort is set per-option below.
  const baseParams = adaptStoreToPageURL(state, 1);
  baseParams.delete('sort');
  const { levels, pathname, routeLiteral } = selectListingsBasePathParts(state);
  const basePath = levels.length > 0 ? buildListingsBasePath(routeLiteral, levels) : pathname;

  return sortOptions.map((option) => {
    const params = new URLSearchParams(baseParams);

    if (option.key) {
      getListingsSortAsURLSearchParams(params, option);
    }

    return {
      active: option.active,
      href: buildListingsURL(basePath, filterPath, params, selectHash(state)),
      title: option.title,
      value: getSortOptionValue(option),
    };
  });
};
